import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, EventAvailable as EventAvailableIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import bookingService from '../../services/bookingService';
import connectionsService from '../../services/connectionsService';
import { deliveryService } from '../../services/deliveryService';
import { flowService } from '../../services/flowService';
import { isBookingFlowJson } from '../../flow-core/booking';
import { track } from '../../utils/analytics';
import { openOAuthPopup } from '../../utils/oauthPopup';
import BookingProvidersPanel from './BookingProvidersPanel';

const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events';
const CALENDAR_CONNECTOR = 'GOOGLE_CALENDAR';
const CALENDAR_CONNECT_POLL_MS = 3000;
const CALENDAR_CONNECT_POLL_TIMEOUT_MS = 120000;

/** Monday first, because a working week is what an owner is editing. */
const DAYS = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

const SLOT_LENGTHS = [15, 20, 30, 45, 60, 90, 120];

/**
 * The next `service_N` id, one past the highest numeric suffix already in use, not the list
 * length, which repeats an id the moment a service in the middle is deleted (e.g. delete
 * service_1 from [service_1, service_2]: length is back to 1, so "Add" would mint service_2
 * again and collide with the one still on screen).
 */
const nextServiceId = (services) => {
  const used = (services || [])
    .map((service) => /^service_(\d+)$/.exec(service.id || ''))
    .filter(Boolean)
    .map((match) => Number(match[1]));
  const next = used.length > 0 ? Math.max(...used) + 1 : (services || []).length + 1;
  return `service_${next}`;
};

/**
 * Settings → Booking.
 *
 * Three things an owner has to be able to do without knowing what a data-exchange endpoint is:
 * turn live booking on, say when they are open, and see who is coming.
 */
const BookingSettings = () => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const { features } = useEntitlements();
  const envId = selectedEnvironment?.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [keyStatus, setKeyStatus] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [saving, setSaving] = useState(false);

  // Whether this deployment ships the Google Calendar connector (GoogleCalendarConnector is a
  // conditional bean, so `/api/connectors` is the honest answer). It ships off while FlowGate
  // waits on Google's verification of the sensitive calendar.events scope: offering "Add calendar
  // access" then would walk an owner through Google's "this app isn't verified" screen to grant
  // access that no destination could use, so the whole card stands down with the connector.
  const [calendarOffered, setCalendarOffered] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);
  const [googleConnection, setGoogleConnection] = useState(null);
  const [bookingFlowSync, setBookingFlowSync] = useState([]); // [{ flow, destination }]
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const calendarPollRef = useRef(null);

  const load = useCallback(async () => {
    if (!envId || !features?.booking) return;
    setLoading(true);
    setError(null);
    try {
      const [status, saved] = await Promise.all([
        bookingService.keyStatus(envId, authenticatedFetch).catch(() => null),
        bookingService.getSchedule(envId, authenticatedFetch),
      ]);
      setKeyStatus(status);
      setSchedule(saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, envId, features?.booking]);

  useEffect(() => { load(); }, [load]);

  /**
   * Whether calendar sync is set up: the environment's Google connection (if any), and, per
   * booking flow, whether it already has a GOOGLE_CALENDAR destination, with that destination's
   * own last-delivery status straight from the destinations API, honest rather than assumed.
   */
  const loadCalendarSync = useCallback(async () => {
    if (!envId) return null;
    setCalendarError(null);
    try {
      const connectors = await deliveryService.listConnectors(authenticatedFetch).catch(() => []);
      const offered = (connectors || []).some((c) => c.type === CALENDAR_CONNECTOR);
      setCalendarOffered(offered);
      // Nothing below can be acted on without the connector, and it is a flow list plus one
      // destinations call per booking flow, so don't spend them to populate a hidden card.
      if (!offered) {
        return null;
      }
      const [connections, flows] = await Promise.all([
        connectionsService.list(envId, authenticatedFetch).catch(() => []),
        flowService.listFlows(envId, authenticatedFetch).catch(() => []),
      ]);
      const google = (connections || []).find((c) => c.provider === 'google') || null;
      setGoogleConnection(google);
      const flowList = Array.isArray(flows) ? flows : (flows?.items ?? []);
      const bookingFlows = flowList.filter((f) => isBookingFlowJson(f.flowJson));
      const perFlow = await Promise.all(bookingFlows.map((flow) =>
        deliveryService.listDestinations(envId, flow.id, authenticatedFetch).catch(() => [])));
      const sync = bookingFlows.map((flow, index) => ({
        flow,
        destination: (perFlow[index] || []).find((d) => d.type === 'GOOGLE_CALENDAR') || null,
      }));
      setBookingFlowSync(sync);
      return { google, sync };
    } catch (e) {
      setCalendarError(e.message || 'Failed to load calendar sync');
      return null;
    }
    // authenticatedFetch is recreated every render; a dependency here would poll in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  useEffect(() => {
    if (!envId) {
      setCalendarLoading(false);
      return undefined;
    }
    setCalendarLoading(true);
    loadCalendarSync().finally(() => setCalendarLoading(false));
    return () => {
      if (calendarPollRef.current) {
        clearInterval(calendarPollRef.current.interval);
        clearTimeout(calendarPollRef.current.timeout);
        calendarPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  const hasCalendarScope = !!googleConnection?.scopes?.includes(CALENDAR_SCOPE);

  const stopCalendarPoll = () => {
    if (calendarPollRef.current) {
      clearInterval(calendarPollRef.current.interval);
      clearTimeout(calendarPollRef.current.timeout);
      calendarPollRef.current = null;
    }
    setConnectingCalendar(false);
  };

  const handleAddCalendarAccess = () => {
    setCalendarError(null);
    // The popup reports back the moment Google answers, so a refusal says so instead of
    // polling in silence for the full timeout. The poll stays as the fallback: the popup can
    // be closed by hand, and the API is what actually knows.
    openOAuthPopup(connectionsService.connectUrl('google', envId, 'calendar'), ({ result }) => {
      if (result === 'connected') {
        loadCalendarSync().finally(stopCalendarPoll);
        return;
      }
      setCalendarError(result === 'denied'
        ? 'Google access was not granted, so bookings cannot reach your calendar yet.'
        : 'Connecting your Google account failed. Try again.');
      stopCalendarPoll();
    });
    setConnectingCalendar(true);
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      const result = await loadCalendarSync();
      const granted = result?.google?.scopes?.includes(CALENDAR_SCOPE);
      if (granted || Date.now() - startedAt > CALENDAR_CONNECT_POLL_TIMEOUT_MS) {
        stopCalendarPoll();
      }
    }, CALENDAR_CONNECT_POLL_MS);
    const timeout = setTimeout(stopCalendarPoll, CALENDAR_CONNECT_POLL_TIMEOUT_MS);
    calendarPollRef.current = { interval, timeout };
  };

  const handleSyncBookingsToCalendar = async () => {
    setSyncingCalendar(true);
    setCalendarError(null);
    try {
      await Promise.all(bookingFlowSync
        .filter(({ destination }) => !destination)
        .map(({ flow }) => deliveryService.createDestination(envId, flow.id, {
          type: 'GOOGLE_CALENDAR',
          config: { calendarId: 'primary' },
          events: ['BOOKING_CREATED', 'BOOKING_CANCELLED'],
        }, authenticatedFetch)));
      await loadCalendarSync();
    } catch (e) {
      setCalendarError(e.message || 'Failed to turn on calendar sync');
    } finally {
      setSyncingCalendar(false);
    }
  };

  const handleRotate = async () => {
    // Captured before the call: this button does double duty ("Set it up now" the first time,
    // "Replace encryption key" ever after), and only the first counts as the funnel event.
    const wasAlreadyEnabled = !!keyStatus?.present;
    setRotating(true);
    setError(null);
    setNotice(null);
    try {
      setKeyStatus(await bookingService.rotateKey(envId, authenticatedFetch));
      setNotice('Live booking is set up on this number.');
      if (!wasAlreadyEnabled) {
        // Launch funnel event (docs/launch/ws-I.md): the owner turned live booking on.
        track('booking_enabled');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setRotating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      setSchedule(await bookingService.saveSchedule(envId, schedule, authenticatedFetch));
      setNotice('Schedule saved.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const patch = (changes) => setSchedule((current) => ({ ...current, ...changes }));

  const setDay = (day, index, field, value) => {
    const hours = { ...(schedule.hours || {}) };
    const ranges = [...(hours[day] || [])];
    ranges[index] = { ...ranges[index], [field]: value };
    hours[day] = ranges;
    patch({ hours });
  };

  const toggleDay = (day) => {
    const hours = { ...(schedule.hours || {}) };
    hours[day] = (hours[day] || []).length > 0 ? [] : [{ from: '09:00', to: '17:00' }];
    patch({ hours });
  };

  const setService = (index, field, value) => {
    const services = [...(schedule.services || [])];
    services[index] = { ...services[index], [field]: value };
    patch({ services });
  };

  if (!envId) {
    return <Alert severity="info">Select an environment to set up booking.</Alert>;
  }
  // Saving any of this is refused on Free (402 booking); until 2026-10-11 the whole screen was
  // offered anyway and failed at the first save
  if (!features?.booking) {
    return (
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="h6">Live booking</Typography>
            <Chip size="small" label="Team" color="secondary" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            With Team, your booking flows show real free times and take the appointment while the
            customer is still in the chat, straight into your schedule.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/settings?tab=billing">
            See Team
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const serverHasNoKey = keyStatus && keyStatus.configured === false;

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="h6">Live booking</Typography>
            {keyStatus?.valid && <Chip size="small" color="success" label="Live" />}
            {keyStatus && keyStatus.present && !keyStatus.valid
              && <Chip size="small" color="warning" label="Needs attention" />}
            {serverHasNoKey && <Chip size="small" label="Not configured" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your booking flows show real free times and take the appointment while the customer is
            still in the chat. This sets itself up the first time you publish a booking flow.
          </Typography>

          {serverHasNoKey ? (
            <Alert severity="info">
              {keyStatus.message
                || 'This server has no booking encryption key, so live booking cannot be switched on here.'}
            </Alert>
          ) : (
            <>
              {keyStatus?.message && (
                <Alert severity={keyStatus.valid ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  {keyStatus.message}
                </Alert>
              )}
              <Button
                variant={keyStatus?.present ? 'text' : 'outlined'}
                size="small"
                onClick={handleRotate}
                disabled={rotating}
              >
                {rotating ? 'Setting up…' : keyStatus?.present ? 'Replace encryption key' : 'Set it up now'}
              </Button>
              {keyStatus?.phoneNumberId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Number {keyStatus.phoneNumberId}
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Where each booking flow books. Given the booking flows the calendar card already found,
          so the two agree on what counts as a booking flow (flow-core decides, not either card). */}
      <BookingProvidersPanel bookingFlows={bookingFlowSync.map(({ flow }) => flow)} />

      {calendarOffered && (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Calendar sync</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Write each booking onto your Google Calendar automatically, and remove it again if it
            is cancelled.
          </Typography>

          {calendarError && (
            <Alert severity="error" onClose={() => setCalendarError(null)} sx={{ mb: 2 }}>
              {calendarError}
            </Alert>
          )}

          {calendarLoading ? (
            <CircularProgress size={20} />
          ) : !hasCalendarScope ? (
            <Button
              variant="outlined"
              startIcon={<EventAvailableIcon />}
              onClick={handleAddCalendarAccess}
              disabled={connectingCalendar}
            >
              {connectingCalendar ? 'Waiting for Google…' : 'Add calendar access'}
            </Button>
          ) : bookingFlowSync.length === 0 ? (
            <Alert severity="info">
              Add a live booking flow first. Calendar sync writes to whichever flows use booking.
            </Alert>
          ) : bookingFlowSync.every(({ destination }) => !destination) ? (
            <Button
              variant="contained"
              onClick={handleSyncBookingsToCalendar}
              disabled={syncingCalendar}
            >
              {syncingCalendar ? 'Turning on…' : 'Sync bookings to Google Calendar'}
            </Button>
          ) : (
            <Stack spacing={1}>
              {bookingFlowSync.filter(({ destination }) => destination).map(({ flow, destination }) => (
                <Stack key={flow.id} direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">
                    Syncing "{flow.name}" to {destination.config?.calendarId || 'primary'}
                  </Typography>
                  <Chip
                    size="small"
                    label={destination.lastStatus || 'Not sent yet'}
                    color={destination.lastStatus === 'SENT' ? 'success'
                      : destination.lastStatus === 'FAILED' ? 'error' : 'default'}
                    variant="outlined"
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      )}

      {schedule && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>When you take bookings</Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth label="Time zone" value={schedule.timezone || ''}
                  onChange={(e) => patch({ timezone: e.target.value })}
                  helperText="e.g. Europe/London"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  select fullWidth label="Slot length" value={schedule.slotMinutes || 30}
                  onChange={(e) => patch({ slotMinutes: Number(e.target.value) })}
                >
                  {SLOT_LENGTHS.map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>{minutes} minutes</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth type="number" label="Gap between bookings"
                  value={schedule.bufferMinutes ?? 0}
                  onChange={(e) => patch({ bufferMinutes: Number(e.target.value) })}
                  helperText="Minutes"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Opening hours</Typography>
            {DAYS.map(([day, label]) => {
              const ranges = schedule.hours?.[day] || [];
              const open = ranges.length > 0;
              return (
                <Stack key={day} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Button size="small" onClick={() => toggleDay(day)} sx={{ width: 120, justifyContent: 'flex-start' }}>
                    {label}
                  </Button>
                  {open ? (
                    <>
                      <TextField
                        size="small" type="time" label="From" value={ranges[0].from || ''}
                        onChange={(e) => setDay(day, 0, 'from', e.target.value)}
                        inputProps={{ 'aria-label': `${label} from` }}
                      />
                      <TextField
                        size="small" type="time" label="To" value={ranges[0].to || ''}
                        onChange={(e) => setDay(day, 0, 'to', e.target.value)}
                        inputProps={{ 'aria-label': `${label} to` }}
                      />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Closed</Typography>
                  )}
                </Stack>
              );
            })}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Days you are closed</Typography>
            <TextField
              fullWidth multiline minRows={2}
              label="Blackout dates"
              value={(schedule.blackoutDates || []).join('\n')}
              onChange={(e) => patch({
                blackoutDates: e.target.value.split('\n').map((line) => line.trim()).filter(Boolean),
              })}
              helperText="One date per line, as 2026-12-25"
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Services</Typography>
            {(schedule.services || []).map((service, index) => (
              <Stack key={service.id || index} direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small" label="Name" value={service.name || ''}
                  onChange={(e) => setService(index, 'name', e.target.value)}
                />
                <TextField
                  size="small" type="number" label="Minutes" sx={{ width: 120 }}
                  value={service.durationMinutes ?? ''}
                  onChange={(e) => setService(index, 'durationMinutes', Number(e.target.value))}
                />
                <IconButton
                  aria-label={`Remove ${service.name || 'service'}`}
                  onClick={() => patch({
                    services: (schedule.services || []).filter((_, i) => i !== index),
                  })}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => patch({
                services: [...(schedule.services || []), {
                  id: nextServiceId(schedule.services),
                  name: '',
                  durationMinutes: schedule.slotMinutes || 30,
                }],
              })}
            >
              Add a service
            </Button>

            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save schedule'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Who is booked in, and when, lives under{' '}
            <Link component={RouterLink} to="/bookings">Bookings</Link>, it is something to look
            at between customers rather than something to set up.
          </Typography>
        </CardContent>
      </Card>

    </Stack>
  );
};

export default BookingSettings;
